import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { take } from 'rxjs/operators';
import firebase from 'firebase/app';
import 'firebase/messaging';
import { constant } from '../constants/constant';

// Khai báo cho TypeScript biết về SignalR trên window object (do dùng script tag)
declare var $: any;

export interface NotificationMessage {
    Id: number;
    Title: string;
    Content: string;
    NotificationType: string;
    CreatedAt: Date;
}

@Injectable({
    providedIn: 'root'
})
export class SignalRHubService {
    private connection: any;
    private proxy: any;
    private isConnected = false;
    private isConnecting = false;

    public onNotificationReceived = new Subject<NotificationMessage>();
    public onConnectionChanged = new Subject<boolean>();

    constructor(
        private afMessaging: AngularFireMessaging,
        private http: HttpClient
    ) {
        // Lắng nghe sự kiện đổi Token một cách an toàn
        this.afMessaging.tokenChanges.subscribe(
            token => {
                if (token) {
                    this.saveTokenToServer(token);
                }
            },
            err => console.warn('⚠️ Không thể lắng nghe tokenChanges (có thể do chưa cấp quyền):', err.message)
        );
    }

    /**
     * Yêu cầu quyền nhận thông báo Push (Firebase)
     * Token này sẽ dùng để bắn thông báo khi tắt Web
     */
    public requestPushPermission(): void {
        const permission = (Notification as any).permission;
        if (permission === 'granted') {
            this.getFcmTokenDirectly();
        } else if (permission === 'denied') {
            console.warn('⚠️ Trình duyệt báo CHẶN (denied). Hãy thử "Đặt lại quyền" và F5 lại trang.');
        } else {
            console.log('📢 Đang yêu cầu lại quyền thông báo...');
            Notification.requestPermission().then(p => {
                console.log('✅ Kết quả xin quyền:', p);
                if (p === 'granted') {
                    this.getFcmTokenDirectly();
                }
            });
        }
    }

    private getFcmTokenDirectly(): void {
        try {
            // Đảm bảo Firebase đã khởi tạo (nếu dùng SDK trực tiếp)
            if (firebase.apps.length === 0) {
                firebase.initializeApp(environment.firebase);
            }

            const messaging = firebase.messaging();
            messaging.getToken({ vapidKey: environment.firebase.vapidKey })
                .then((token) => {
                    if (token) {
                        this.saveTokenToServer(token);
                    }
                })
                .catch((err) => {
                    console.error('❌ Lỗi lấy Token từ Firebase SDK:', err);
                });
        } catch (error) {
            console.error('❌ Lỗi khởi tạo Messaging:', error);
        }
    }

    public getOrCreateDeviceId(): string {
        let deviceId = localStorage.getItem('fcm_device_id');
        if (!deviceId) {
            deviceId = 'web_' + Math.random().toString(36).substring(2, 15);
            localStorage.setItem('fcm_device_id', deviceId);
        }
        return deviceId;
    }

    public saveTokenToServer(token?: string): void {
        const firebaseToken = token || localStorage.getItem(constant.FIREBASE_TOKEN);
        if (!firebaseToken) return;

        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) return;

        const headers = new HttpHeaders().set('Authorization', 'Bearer ' + accessToken);

        const deviceId = this.getOrCreateDeviceId();

        const url = `${environment.apiUri}/api/notification-firebase/register-token`;
        this.http.post(url, {
            FirebaseToken: firebaseToken,
            DeviceId: deviceId,
            OsPlatform: 'Web',
            OsVersion: window.navigator.userAgent.substring(0, 50)
        }, { headers }).subscribe(
        );
    }

    /**
     * Khởi tạo kết nối SignalR Hub (phiên bản 2.x)
     */
    public startConnection(): void {
        if (this.isConnected || this.isConnecting) return;
        this.isConnecting = true;

        const token = localStorage.getItem('access_token');

        // Khởi tạo connection với token trong query string để Server nhận diện User
        this.connection = $.hubConnection(`${environment.apiUri}`, {
            qs: { token: token }
        });

        // Tên của Hub (phải khớp với [HubName("notificationHub")] trên server)
        this.proxy = this.connection.createHubProxy('notificationHub');

        // Đăng ký listener từ Server gửi xuống
        this.proxy.on('receiveNotification', (title: string, payloadJson: string) => {
            try {
                const payload = JSON.parse(payloadJson);
                this.onNotificationReceived.next(payload);
            } catch (e) {
                console.error('Error parsing notification payload:', e);
            }
        });

        // Bật log để debug
        this.connection.logging = true;

        // Bắt đầu connection
        this.connection.start()
            .done(() => {
                this.isConnected = true;
                this.isConnecting = false;
                this.onConnectionChanged.next(true);

                // Sau khi connect SignalR xong thì xin quyền Push luôn
                this.requestPushPermission();
            })
            .fail((error: any) => {
                console.error('❌ SignalR 2.x Connection Error:', error);
                this.isConnected = false;
                this.isConnecting = false;
                this.onConnectionChanged.next(false);
            });

        // Xử lý khi bị ngắt kết nối
        this.connection.disconnected(() => {
            console.warn('⚠️ SignalR 2.x Disconnected');
            this.isConnected = false;
            this.isConnecting = false;
            this.onConnectionChanged.next(false);

            // Thử kết nối lại sau 5 giây
            setTimeout(() => this.startConnection(), 5000);
        });
    }

    /**
   * Ngắt kết nối SignalR
   */
    public stopConnection(): void {
        this.unregisterPushToken(); // Hủy đăng ký Push Token trước khi ngắt kết nối
        if (this.connection) {
            this.connection.stop();
            this.isConnected = false;
        }
    }

    /**
     * Hủy đăng ký Firebase Token trên Server
     */
    private unregisterPushToken(): void {
        try {
            if (firebase.apps.length > 0) {
                const messaging = firebase.messaging();
                messaging.getToken().then(token => {
                    if (token) {
                        const accessToken = localStorage.getItem('access_token');
                        const headers = new HttpHeaders().set('Authorization', 'Bearer ' + accessToken);
                        const url = `${environment.apiUri}/api/notification-firebase/unregister-token`;

                        // Gửi token dưới dạng string (Request Body)
                        this.http.post(url, `"${token}"`, {
                            headers: headers.set('Content-Type', 'application/json')
                        }).subscribe(
                            () => console.log('✅ Đã hủy đăng ký Push Token trên Server'),
                            err => console.error('❌ Lỗi hủy đăng ký Push Token', err)
                        );
                    }
                });
            }
        } catch (error) {
            console.error('❌ Lỗi khi hủy đăng ký Push Token:', error);
        }
    }

    public getConnectionState(): boolean {
        return this.isConnected;
    }
}
